import React from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Mail, Phone, MapPin } from 'lucide-react';
import { businessInfo } from '../mock';

const footerLinks = [
  { to: '/', label: 'Home' },
  { to: '/about', label: 'About Us' },
  { to: '/services', label: 'Services' },
  { to: '/gallery', label: 'Project/Portfolio' },
  { to: '/reviews', label: 'Reviews' },
  { to: '/booking', label: 'Book Online' },
  { to: '/contact', label: 'Contact' },
];

const legalLinks = [
  { href: 'https://www.iubenda.com/privacy-policy/91226457', label: 'Privacy Policy' },
  { href: 'https://www.iubenda.com/privacy-policy/91226457/cookie-policy', label: 'Cookie Policy' },
  { href: 'https://www.iubenda.com/terms-and-conditions/91226457', label: 'Terms and Conditions' },
];

const FooterCompanyInfo = () => (
  <div className="md:col-span-2">
    <div className="flex items-center gap-4 mb-4">
      <img src={businessInfo.logo} alt={businessInfo.name} className="h-40 w-40 object-contain" />
      <h3 className="text-2xl font-bold text-white">{businessInfo.name}</h3>
    </div>
    <p className="text-slate-400 mb-6 leading-relaxed max-w-md">
      Your trusted plumbing experts serving the Melbourne area with over 15 years of experience.
      Quality workmanship, reliable service, and customer satisfaction guaranteed.
    </p>
    <div className="flex gap-3">
      <a
        href="https://www.instagram.com/aaplumbingroof/"
        target="_blank"
        rel="noopener noreferrer"
        className="bg-slate-800 hover:bg-blue-600 p-3 rounded-lg transition-colors duration-200"
        aria-label="Instagram"
      >
        <Instagram size={20} />
      </a>
    </div>
  </div>
);

const FooterQuickLinks = () => (
  <div>
    <h4 className="text-white font-bold mb-4 text-lg">Quick Links</h4>
    <ul className="space-y-3">
      {footerLinks.map((link) => (
        <li key={link.to}>
          <Link to={link.to} className="hover:text-blue-400 transition-colors duration-200">
            {link.label}
          </Link>
        </li>
      ))}
    </ul>
  </div>
);

const FooterContact = () => (
  <div>
    <h4 className="text-white font-bold mb-4 text-lg">Contact Us</h4>
    <ul className="space-y-3">
      <li className="flex items-start gap-3">
        <Phone size={18} className="mt-1 flex-shrink-0" />
        <a href={`tel:${businessInfo.phoneRaw}`} className="hover:text-blue-400 transition-colors duration-200">
          {businessInfo.phone}
        </a>
      </li>
      <li className="flex items-start gap-3">
        <Mail size={18} className="mt-1 flex-shrink-0" />
        <a href={`mailto:${businessInfo.email}`} className="hover:text-blue-400 transition-colors duration-200 break-all">
          {businessInfo.email}
        </a>
      </li>
      <li className="flex items-start gap-3">
        <MapPin size={18} className="mt-1 flex-shrink-0" />
        <span>
          {businessInfo.address}<br />
          {businessInfo.city}, {businessInfo.state} {businessInfo.zipCode}
        </span>
      </li>
    </ul>
  </div>
);

const FooterLegalBar = ({ year }) => (
  <div className="border-t border-slate-800 pt-8">
    <div className="flex flex-col md:flex-row justify-between items-center gap-4">
      <p className="text-slate-500 text-sm">
        © {year} {businessInfo.name}. All rights reserved.
      </p>
      <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
        {legalLinks.map((link) => (
          <a
            key={link.href}
            href={link.href}
            className="iubenda-black no-brand iubenda-noiframe iubenda-embed hover:text-blue-400 transition-colors duration-200"
            title={link.label}
          >
            {link.label}
          </a>
        ))}
      </div>
    </div>
  </div>
);

const Footer = () => {
  const currentYear = new Date().getFullYear();
  return (
    <footer data-testid="footer" className="bg-slate-900 text-slate-300 pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-4 gap-8 mb-12">
          <FooterCompanyInfo />
          <FooterQuickLinks />
          <FooterContact />
        </div>
        <FooterLegalBar year={currentYear} />
      </div>
    </footer>
  );
};

export default Footer;
